import React, { useState } from 'react';
import { type BoardingHouse, RoomStatus, ResidencyStatus } from '../types';

interface BackupRestoreProps {
    boardingHouses: BoardingHouse[];
    setBoardingHouses: React.Dispatch<React.SetStateAction<BoardingHouse[]>>;
    geminiApiKey: string;
    setGeminiApiKey: React.Dispatch<React.SetStateAction<string>>;
}

const BackupRestore: React.FC<BackupRestoreProps> = ({
    boardingHouses,
    setBoardingHouses,
    geminiApiKey,
    setGeminiApiKey,
}) => {
    const [fileName, setFileName] = useState<string | null>(null);
    const [restoreError, setRestoreError] = useState<string | null>(null);
    const [restoreSuccess, setRestoreSuccess] = useState<string | null>(null);

    const handleBackup = () => {
        const backupData = {
            version: "2.0", // New version for multi-house structure
            boardingHouses,
            geminiApiKey,
        };

        const dataStr = JSON.stringify(backupData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        const now = new Date();
        const date = now.toISOString().split('T')[0];
        const time = now.toLocaleTimeString('en-GB').replace(/:/g, '-');
        link.download = `ivs-staysmart-backup-${date}_${time}.json`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRestoreError(null);
        setRestoreSuccess(null);
        const file = event.target.files?.[0];
        if (file) {
            if (file.type !== 'application/json') {
                setRestoreError('Lỗi: Vui lòng chỉ chọn file backup có định dạng .json.');
                setFileName(null);
                return;
            }
            setFileName(file.name);
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result;
                    if (typeof text !== 'string') throw new Error("File content is not readable.");

                    const parsedData = JSON.parse(text);
                    
                    const performRestore = () => {
                        // New multi-house format (v2.0)
                        if (parsedData.version === '2.0' && Array.isArray(parsedData.boardingHouses)) {
                            // Fix RoomStatus values from backup (convert from string to enum)
                            const fixedBoardingHouses = parsedData.boardingHouses.map((house: any) => ({
                                ...house,
                                rooms: house.rooms.map((room: any) => ({
                                    ...room,
                                    status: room.status === 'Occupied' ? RoomStatus.Occupied : 
                                           room.status === 'Available' ? RoomStatus.Available :
                                           room.status === 'Maintenance' ? RoomStatus.Maintenance : RoomStatus.Available
                                }))
                            }));
                            
                            // Fix ResidencyStatus values from backup
                            const fixedBoardingHousesWithResidency = fixedBoardingHouses.map((house: any) => ({
                                ...house,
                                residencyRegistrations: house.residencyRegistrations.map((reg: any) => ({
                                    ...reg,
                                    status: reg.status === 'Chãng ký ký' ? ResidencyStatus.Unregistered :
                                           reg.status === 'Ðã ký ký' ? ResidencyStatus.Registered :
                                           reg.status === 'Hát hán' ? ResidencyStatus.Expired : ResidencyStatus.Unregistered
                                }))
                            }));
                            
                            setBoardingHouses(fixedBoardingHousesWithResidency);
                            setGeminiApiKey(parsedData.geminiApiKey || '');
                            setRestoreSuccess(`Ðã khôi phá toàn bá dá liáu tà file ${file.name}.`);
                        // Legacy single-house format (migration)
                        } else if (parsedData.rooms && parsedData.tenants && !parsedData.version) {
                            const migratedHouse: BoardingHouse = {
                                id: `BH_migrated_${Date.now()}`,
                                info: {
                                    name: "Nhà trọ đã khôi phục",
                                    address: "", phone: "", landlordName: "", landlordDob: "", landlordIdNumber: "",
                                    localPoliceDepartment: "", defaultRoomPrice: 0,
                                    numberOfRooms: parsedData.rooms.length
                                },
                                rooms: parsedData.rooms,
                                tenants: parsedData.tenants,
                                roomMembers: parsedData.roomMembers || [],
                                residencyRegistrations: parsedData.residencyRegistrations || []
                            };
                            setBoardingHouses([migratedHouse]);
                            setGeminiApiKey(''); // API key not in old format
                            setRestoreSuccess(`Đã khôi phục và di chuyển dữ liệu từ file backup cũ "${file.name}" thành một nhà trọ duy nhất.`);
                        } else {
                            throw new Error("Định dạng file backup không được hỗ trợ hoặc file bị hỏng.");
                        }
                    };

                    if (window.confirm('Bạn có chắc chắn muốn khôi phục dữ liệu từ file này? Mọi dữ liệu hiện tại sẽ bị ghi đè.')) {
                        performRestore();
                    }

                } catch (error: any) {
                    setRestoreError(`Lỗi khi xử lý file: ${error.message}.`);
                }
            };
            reader.onerror = () => {
                 setRestoreError("Không thể đọc file đã chọn.");
            }
            reader.readAsText(file);
        } else {
             setFileName(null);
        }
        event.target.value = '';
    };

    return (
        <div className="space-y-8 max-w-2xl mx-auto">
            <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Sao lưu Toàn bộ Dữ liệu</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Nhấn nút bên dưới để tải về một file backup duy nhất chứa dữ liệu của TẤT CẢ các nhà trọ và cài đặt ứng dụng của bạn.
                </p>
                <button
                    onClick={handleBackup}
                    className="w-full sm:w-auto px-6 py-3 font-medium tracking-wide text-white capitalize transition-colors duration-200 transform bg-primary-600 rounded-lg hover:bg-primary-500 focus:outline-none focus:ring focus:ring-primary-300 focus:ring-opacity-80"
                >
                    Tải về bản Sao lưu
                </button>
            </div>

            <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Khôi phục Dữ liệu</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Chọn một file sao lưu (.json) bạn đã tải về trước đó để khôi phục lại toàn bộ trạng thái của ứng dụng.
                </p>
                <div className="p-4 border-2 border-dashed border-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300 mb-4">
                    <span className="font-bold">CẢNH BÁO:</span> Hành động này sẽ <span className="font-bold">thay thế TOÀN BỘ</span> dữ liệu hiện tại bằng dữ liệu trong file backup.
                </div>
                
                <div className="mt-4">
                    <label
                        htmlFor="file-upload"
                        className="cursor-pointer inline-flex items-center px-6 py-3 font-medium tracking-wide text-white capitalize transition-colors duration-200 transform bg-green-600 rounded-lg hover:bg-green-500 focus:outline-none focus:ring focus:ring-green-300"
                    >
                        Chọn file để Khôi phục
                    </label>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".json" onChange={handleFileChange} />
                    {fileName && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">File đã chọn: <span className="font-medium">{fileName}</span></p>}
                </div>

                {restoreError && <p className="mt-4 text-sm font-semibold text-red-600 dark:text-red-400">{restoreError}</p>}
                {restoreSuccess && <p className="mt-4 text-sm font-semibold text-green-600 dark:text-green-400">{restoreSuccess}</p>}
            </div>
        </div>
    );
};

export default BackupRestore;